import * as echarts from 'echarts';
import type { ChartConfig, ChartPalette, EasingType } from './types';

const EASING: Record<EasingType, (t: number) => number> = {
    linear: (t) => t,
    easeIn: (t) => t * t,
    easeOut: (t) => t * (2 - t),
    easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    easeInCubic: (t) => t * t * t,
    easeOutCubic: (t) => --t * t * t + 1,
    easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
    easeOutElastic: (t) => {
        if (t === 0) return 0;
        if (t === 1) return 1;
        return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
    },
    easeOutBounce: (t) => {
        if (t < 1 / 2.75) return 7.5625 * t * t;
        if (t < 2 / 2.75) return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
        if (t < 2.5 / 2.75) return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
        return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
    },
    easeOutBack: (t) => 1 + 2.70158 * Math.pow(t - 1, 3) + 1.70158 * Math.pow(t - 1, 2),
};

export class ChartRenderer {
    #instance: echarts.ECharts | null = null;
    #currentCanvas: HTMLCanvasElement | null = null;

    renderToCanvas(
        canvas: HTMLCanvasElement,
        config: ChartConfig,
        progress: number,
        palette: ChartPalette,
    ): void {
        const p = Math.max(0, Math.min(1, progress));
        const globalEasing = config.easing || 'linear';

        const resolved = this.#resolveAllRefs(config.echarts, palette);
        const finalState = this.#interpolateAnimated(resolved, 1, globalEasing) as Record<
            string,
            unknown
        >;
        const interpolated = this.#interpolateAnimated(resolved, p, globalEasing) as Record<
            string,
            unknown
        >;
        interpolated.animation = false;
        this.#stabilizeAxes(interpolated, finalState);

        this.#revealLineSeries(interpolated, finalState, p);

        this.#ensureInstance(canvas);

        this.#instance!.setOption(interpolated, true);
    }

    destroy(): void {
        if (this.#instance) {
            this.#instance.dispose();
            this.#instance = null;
        }
        this.#currentCanvas = null;
    }

    #revealLineSeries(
        interpolated: Record<string, unknown>,
        finalState: Record<string, unknown>,
        progress: number,
    ): void {
        const interpSeries = interpolated.series as Record<string, unknown>[] | undefined;
        const finalSeries = finalState.series as Record<string, unknown>[] | undefined;
        if (!interpSeries || !finalSeries) return;

        const finalXAxis = finalState.xAxis as Record<string, unknown> | undefined;
        const categories = finalXAxis?.data as string[] | undefined;
        if (!categories || categories.length < 2) return;

        const totalSteps = 50;
        const stepsToShow = Math.max(1, Math.round(progress * totalSteps));
        const expandedCategories = this.#buildExpandedCategories(categories, totalSteps);

        let hasLineReveal = false;

        for (let i = 0; i < interpSeries.length; i++) {
            if (interpSeries[i].type !== 'line') continue;

            const finalData = finalSeries[i]?.data;
            if (!Array.isArray(finalData)) continue;

            const numericData = finalData.map((item) => {
                if (typeof item === 'number') return item;
                if (item && typeof item === 'object' && 'value' in item) {
                    return typeof item.value === 'number' ? item.value : 0;
                }
                return 0;
            });

            interpSeries[i].data = this.#buildRevealData(numericData, totalSteps, stepsToShow);
            if (progress < 1) {
                interpSeries[i].smooth = false;
            }
            hasLineReveal = true;
        }

        if (hasLineReveal) {
            const interpXAxis = interpolated.xAxis as Record<string, unknown> | undefined;
            if (interpXAxis) {
                interpXAxis.data = expandedCategories;
            }
        }
    }

    #buildExpandedCategories(categories: string[], totalSteps: number): string[] {
        const n = categories.length;
        const stepX = (n - 1) / totalSteps;

        return Array.from({ length: totalSteps + 1 }, (_, s) => {
            const x = s * stepX;
            const idx = Math.round(x);
            return Math.abs(x - idx) < 0.001 ? (categories[idx] ?? '') : '';
        });
    }

    #buildRevealData(data: number[], totalSteps: number, stepsToShow: number): (number | null)[] {
        const n = data.length;
        const stepX = (n - 1) / totalSteps;

        return Array.from({ length: totalSteps + 1 }, (_, s) => {
            if (s > stepsToShow) return null;
            const x = Math.min(s * stepX, n - 1);
            const segIdx = Math.min(Math.floor(x), n - 2);
            const frac = x - segIdx;
            return data[segIdx] + (data[segIdx + 1] - data[segIdx]) * frac;
        });
    }

    #ensureInstance(canvas: HTMLCanvasElement): void {
        const width = canvas.width;
        const height = canvas.height;

        if (this.#currentCanvas !== canvas) {
            if (this.#instance) {
                this.#instance.dispose();
            }
            this.#instance = echarts.init(canvas, undefined, { width, height });
            this.#currentCanvas = canvas;
        } else {
            this.#instance!.resize({ width, height });
        }
    }

    #stabilizeAxes(
        interpolated: Record<string, unknown>,
        finalState: Record<string, unknown>,
    ): void {
        this.#copyAxisRange(interpolated, finalState, 'xAxis');
        this.#copyAxisRange(interpolated, finalState, 'yAxis');
    }

    #copyAxisRange(
        interpolated: Record<string, unknown>,
        finalState: Record<string, unknown>,
        key: string,
    ): void {
        const finalAxis = finalState[key] as Record<string, unknown> | undefined;
        const interpAxis = interpolated[key] as Record<string, unknown> | undefined;
        if (!finalAxis || !interpAxis) return;

        if (finalAxis.min !== undefined) interpAxis.min = finalAxis.min;
        if (finalAxis.max !== undefined) interpAxis.max = finalAxis.max;

        if (interpAxis.min === undefined || interpAxis.max === undefined) {
            const range = this.#computeAxisRange(finalState, key);
            if (range) {
                if (interpAxis.min === undefined) interpAxis.min = range.min;
                if (interpAxis.max === undefined) interpAxis.max = range.max;
            }
        }
    }

    #computeAxisRange(
        state: Record<string, unknown>,
        axisKey: string,
    ): { min: number; max: number } | null {
        const axis = state[axisKey] as Record<string, unknown> | undefined;
        if (!axis || axis.type !== 'value') return null;

        const series = state.series as Record<string, unknown>[] | undefined;
        if (!series) return null;

        let min = Infinity;
        let max = -Infinity;

        for (const s of series) {
            const data = s.data;
            if (Array.isArray(data)) {
                for (const item of data) {
                    const val =
                        typeof item === 'number' ? item : (item as Record<string, unknown>)?.value;
                    if (typeof val === 'number') {
                        min = Math.min(min, val);
                        max = Math.max(max, val);
                    }
                }
            }
        }

        if (min === Infinity) return null;

        const padding = (max - min) * 0.1 || max * 0.1 || 1;
        return {
            min: Math.min(0, min),
            max: max + padding,
        };
    }

    #resolveThemeRef(value: unknown, palette: ChartPalette): unknown {
        if (typeof value !== 'string') return value;

        const colorMatch = value.match(/^themeRef_colors_(\d+)$/);
        if (colorMatch && palette?.colors) {
            const index = parseInt(colorMatch[1], 10);
            return palette.colors[index] || value;
        }

        const refMatch = value.match(/^themeRef_(\w+)$/);
        if (refMatch && palette) {
            const key = refMatch[1] as keyof ChartPalette;
            return palette[key] || value;
        }

        return value;
    }

    #resolveAllRefs<T>(obj: T, palette: ChartPalette): T {
        if (obj === null || obj === undefined) return obj;

        if (typeof obj === 'string') {
            return this.#resolveThemeRef(obj, palette) as T;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.#resolveAllRefs(item, palette)) as T;
        }

        if (typeof obj === 'object') {
            const result: Record<string, unknown> = {};
            for (const key in obj) {
                result[key] = this.#resolveAllRefs((obj as Record<string, unknown>)[key], palette);
            }
            return result as T;
        }

        return obj;
    }

    #lerp(a: unknown, b: unknown, t: number): unknown {
        if (typeof a === 'number' && typeof b === 'number') {
            return a + (b - a) * t;
        }

        if (Array.isArray(a) && Array.isArray(b)) {
            return b.map((bVal, i) => this.#lerp(a[i], bVal, t));
        }

        return t < 0.5 ? a : b;
    }

    #interpolateAnimated<T>(obj: T, progress: number, globalEasing: EasingType): T {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;

        if (this.#isAnimatedValue(obj)) {
            const animateObj = obj as unknown as {
                $animate: { from: unknown; to: unknown; easing?: EasingType };
            };
            const { from, to, easing } = animateObj.$animate;
            const easingFn = EASING[easing || globalEasing] || EASING.linear;
            return this.#lerp(from, to, easingFn(progress)) as T;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.#interpolateAnimated(item, progress, globalEasing)) as T;
        }

        const result: Record<string, unknown> = {};
        for (const key in obj) {
            result[key] = this.#interpolateAnimated(
                (obj as Record<string, unknown>)[key],
                progress,
                globalEasing,
            );
        }
        return result as T;
    }

    #isAnimatedValue(obj: unknown): boolean {
        return typeof obj === 'object' && obj !== null && '$animate' in obj;
    }
}
